import { ScheduledQuery, ScheduledQueryConversation } from '@api';
import { Alert, AlertType } from '@design/components/alert/Alert';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { Icon } from '@design/components/icon/Icon';
import { Modal } from '@design/components/modal/Modal';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import {
  useDeleteScheduledQuery,
  useSuspendScheduledQuery,
} from '@terminal/react-query-hooks/alfred/useScheduledQueryMutations';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { formatModalCadence } from '@terminal/views/alfred/research/utils/formatCadence';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './DeleteScheduledTaskModal.module.scss';

interface DeleteScheduledTaskModalProps {
  task: ScheduledQuery;
  conversations?: ScheduledQueryConversation[];
  defaultDeleteConversations?: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteScheduledTaskModal = ({
  task,
  conversations,
  defaultDeleteConversations = false,
  onClose,
  onDeleted,
}: DeleteScheduledTaskModalProps) => {
  const { t } = useTranslation(['chat']);
  const { mutate: deleteQuery, isPending: isDeletingTask } =
    useDeleteScheduledQuery();
  const { mutate: suspendQuery, isPending: isSuspending } =
    useSuspendScheduledQuery();
  const [alsoDeleteConversations, setAlsoDeleteConversations] = useState(
    defaultDeleteConversations
  );
  const [isDeletingConversations, setIsDeletingConversations] = useState(false);
  const isPending = isDeletingTask || isSuspending || isDeletingConversations;

  const { showToast, removeToast } = useToasts(state => ({
    showToast: state.showToast,
    removeToast: state.removeToast,
  }));

  const handleSuccess = async () => {
    if (alsoDeleteConversations && conversations?.length) {
      setIsDeletingConversations(true);
      const results = await Promise.allSettled(
        conversations.map(c =>
          useChat.getState().deletePrivateConversation(c.id)
        )
      );
      setIsDeletingConversations(false);

      const anyFailed = results.some(r => r.status === 'rejected');
      if (anyFailed) {
        const warnId = `task-delete-warn-${Date.now()}`;
        showToast({
          id: warnId,
          position: ToastPosition.TopRight,
          content: (
            <Alert
              type={AlertType.Error}
              title={t(
                'chat:scheduledQuerySettings.toastConvDeleteFailedTitle'
              )}
              message={t(
                'chat:scheduledQuerySettings.toastConvDeleteFailedMessage'
              )}
              onClose={() => removeToast(warnId)}
            />
          ),
        });
      }
    }

    const toastId = `task-deleted-${Date.now()}`;
    showToast({
      id: toastId,
      position: ToastPosition.TopRight,
      content: (
        <Alert
          type={AlertType.Success}
          title={t('chat:scheduledQuerySettings.toastTaskDeletedTitle')}
          message={t('chat:scheduledQuerySettings.toastTaskDeletedMessage')}
          onClose={() => removeToast(toastId)}
        />
      ),
    });
    onDeleted();
    onClose();
  };

  const handleError = () => {
    const errId = `task-delete-error-${Date.now()}`;
    showToast({
      id: errId,
      position: ToastPosition.TopRight,
      content: (
        <Alert
          type={AlertType.Error}
          title={t('chat:scheduledQuerySettings.toastTaskDeleteFailedTitle')}
          message={t(
            'chat:scheduledQuerySettings.toastTaskDeleteFailedMessage'
          )}
          onClose={() => removeToast(errId)}
        />
      ),
    });
  };

  const handleDelete = () => {
    if (alsoDeleteConversations) {
      // Hard-delete the task (and conversations will be deleted in handleSuccess)
      deleteQuery(task.id, { onSuccess: handleSuccess, onError: handleError });
    } else {
      // Suspend the task so history remains accessible; no 404s for linked conversations
      suspendQuery(task.id, { onSuccess: handleSuccess, onError: handleError });
    }
  };

  return (
    <Modal
      isOpen
      title={t('chat:scheduledQuerySettings.deleteModalTitle')}
      onClose={onClose}
      cancelBtnProps={{
        label: t('chat:scheduledQuerySettings.cancel'),
        onClick: onClose,
      }}
      primaryBtnProps={{
        label: t(
          alsoDeleteConversations
            ? 'chat:scheduledQuerySettings.deleteHistory'
            : 'chat:scheduledQuerySettings.deleteTask'
        ),
        onClick: handleDelete,
        disabled: isPending,
        isLoading: isPending,
        variant: 'critical',
      }}
    >
      <div className={styles.body}>
        <p className={styles.description}>
          {t('chat:scheduledQuerySettings.deleteModalDescription')}
        </p>

        <div className={styles.taskCard}>
          {/* Top section — task info */}
          <div className={styles.taskCardTop}>
            <div className={styles.taskTitleRow}>
              <Icon iconName="Clock" size={20} />
              <TooltipContainer
                label={
                  <span className={styles.tooltipLabel}>{task.title}</span>
                }
                placement="right"
                inPortal
              >
                <span className={styles.taskTitle}>{task.title}</span>
              </TooltipContainer>
            </div>
            <div className={styles.taskCadenceRow}>
              <span className={styles.taskCadence}>
                {formatModalCadence(task)}
              </span>
            </div>
          </div>

          {/* Checkbox row */}
          <div className={styles.taskCardBottom}>
            <CheckBox
              name="also-delete-conversations"
              label={t('chat:scheduledTaskDetail.alsoDeleteConversations')}
              checked={alsoDeleteConversations}
              variant={defaultDeleteConversations ? 'disabled' : 'primary'}
              onChange={(_, e) => setAlsoDeleteConversations(e.target.checked)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
